/**
 * Страница открыта в окне приложения MY REVIVE, а не во вкладке браузера.
 *
 * Приложение открывает любую страницу сайта с ?source=app (см.
 * features/auth/lib/webHandoff.ts) — но только когда у него есть вход; без
 * входа адрес идёт без метки. Поэтому второй признак — referrer, который
 * Chrome ставит окну, открытому приложением: android-app://com.torsunov.revive
 * (так и у уже выпущенных сборок). Признак есть только в первом адресе
 * окна, поэтому его запоминает sessionStorage этой вкладки: переходы по сайту
 * внутри окна его не теряют, а обычная вкладка браузера его не получает.
 *
 * В таком окне у кабинета нет своей шапки — только «‹», как в приложении
 * (AppWindowBar). «‹» на первой странице окна возвращает в приложение по
 * ссылке [APP_CLOSE_URL]: приложение закрывает окно сайта и показывает экран,
 * с которого его открыли.
 */
import { HANDOFF_SOURCE_APP, HANDOFF_SOURCE_PARAM } from "@/features/auth/lib/webHandoff";
import { getAdminPreviewAsUser, isAdmin } from "@/shared/config/admin";

const KEY = "revive_app_window";

/** Chrome ставит окну, открытому приложением, referrer android-app://<пакет>/. */
const APP_REFERRER = /^android-app:\/\/com\.torsunov\.revive(\/|$)/;

/** Ссылка, по которой приложение закрывает окно сайта (обрабатывает само приложение). */
export const APP_CLOSE_URL = "myrevive://app/close";

/**
 * Ключ localStorage: на этом телефоне есть приложение MY REVIVE — оно уже
 * открывало здесь окно сайта. Окно приложения (Trusted Web Activity) — это
 * тот же Chrome телефона с тем же хранилищем, поэтому отметка, поставленная
 * в окне, видна и в обычной вкладке: там «Тренировки» и «Календарь» зовут
 * «Открыть в приложении», а не «Установить» (useHasApp). Значение — когда
 * отметили (ISO).
 */
export const HAS_APP_KEY = "revive_has_app";

/**
 * Как приложение Google Play открыло страницу (владелец 04.11: вне России сайт открывается во
 * вкладке Chrome, продажи продуктов сайта — там, по ссылке). Новые сборки ставят в каждый адрес
 * torsunov.com, рядом с source=app, app_view=window|tab|browser и app_region=ru|intl
 * (revive-android-phone claude/gp-site-in-chrome). Оба признака есть только в первом адресе
 * вкладки — их запоминает sessionStorage, как и [isAppWindow].
 */
export type AppView = "window" | "tab" | "browser";
export type AppRegion = "ru" | "intl";

const VIEW_PARAM = "app_view";
const REGION_PARAM = "app_region";
const VIEW_KEY = "revive_app_view";
const REGION_KEY = "revive_app_region";
const VIEWS: readonly AppView[] = ["window", "tab", "browser"];
const REGIONS: readonly AppRegion[] = ["ru", "intl"];

/** Значение параметра из адреса (запоминается на вкладку), иначе запомненное, иначе null. */
const tabMark = <T extends string>(param: string, key: string, allowed: readonly T[]): T | null => {
    try {
        const fromUrl = new URLSearchParams(window.location.search).get(param) as T | null;
        if (fromUrl && allowed.includes(fromUrl)) {
            sessionStorage.setItem(key, fromUrl);
            return fromUrl;
        }
        const kept = sessionStorage.getItem(key) as T | null;
        return kept && allowed.includes(kept) ? kept : null;
    } catch {
        return null;
    }
};

/** Где открыло страницу приложение: окно (TWA), вкладка Chrome, обычный браузер; null — не сказано. */
export const appView = (): AppView | null => tabMark(VIEW_PARAM, VIEW_KEY, VIEWS);

/** Регион приложения, открывшего страницу (по правилам Google Play); null — не сказано. */
export const appRegion = (): AppRegion | null => tabMark(REGION_PARAM, REGION_KEY, REGIONS);

/**
 * Рубильник: новое поведение страниц из приложения Google Play (вкладка — обычный сайт с нижним
 * меню, продажи сайта, без подписки MY REVIVE) — всем. Пока false: только админам (владелец 04.11:
 * «сначала мы пробуем все на учетке админа на ком для ГП версии»); остальные — как раньше.
 */
export const GP_TAB_FOR_ALL = false;

/**
 * Админ на этом браузере — без запроса списка: адрес входа (site_user_email) в основном списке
 * или тот, кого оболочка запомнила подтверждённым админом (APP_SHELL_ADMIN_HINT_KEY,
 * features/appShell/model/config.ts). «Смотреть как пользователь» — не админ.
 */
const adminHere = (): boolean => {
    try {
        if (getAdminPreviewAsUser()) return false;
        const email = (localStorage.getItem("site_user_email") || "").toLowerCase();
        if (!email) return false;
        return isAdmin(email) || (localStorage.getItem("app_shell_admin") || "").toLowerCase() === email;
    } catch {
        return false;
    }
};

/** Действуют ли app_view / app_region для этого человека ([GP_TAB_FOR_ALL] или админ). */
export const gpTabModeOn = (): boolean => GP_TAB_FOR_ALL || adminHere();

/**
 * Открыта ли страница в окне приложения; видит метку в адресе — запоминает её.
 * Сказано app_view (и [gpTabModeOn]) — решает он: tab и browser — обычный сайт (нижнее меню, все
 * продажи сайта), даже рядом с source=app. Не сказано (сборки до 04.11) — по source=app или
 * referrer, как раньше.
 */
export const isAppWindow = (): boolean => {
    try {
        const marked =
            new URLSearchParams(window.location.search).getAll(HANDOFF_SOURCE_PARAM).includes(HANDOFF_SOURCE_APP) ||
            APP_REFERRER.test(document.referrer || "");
        if (marked) markHasApp();
        // Оба признака — с первого адреса вкладки (App.tsx спрашивает isAppWindow на старте):
        // регион нужен и тем страницам, куда человек придёт потом (/buy-subscription).
        appRegion();
        const view = appView();
        if (view && gpTabModeOn()) return view === "window";
        if (marked) {
            sessionStorage.setItem(KEY, "1");
            return true;
        }
        return sessionStorage.getItem(KEY) === "1";
    } catch {
        return false;
    }
};

/**
 * Пакеты с годом приложения MY REVIVE и сам год (как gifts GIFTABLE_PACKAGE_IDS: эти пакеты
 * выдают год подписки).
 */
export const APP_YEAR_PACKAGE_IDS: readonly string[] = [
    "pack-2-system", "pack-pdf-audio-app", "pack-3-mass", "pack-app-subscription-1y",
];

/**
 * Продаём ли здесь подписку MY REVIVE. Страницу открыло приложение Google Play (сказан
 * app_region) — нет: подписку там продаёт только Google (владелец 04.11). Ни кнопок, ни
 * /buy-subscription, ни пакетов с годом приложения; остальные продукты сайта продаются.
 */
export const subscriptionSalesHere = (): boolean => !(gpTabModeOn() && appRegion() !== null);

/** Продаём ли здесь этот пакет: пакеты с годом приложения — только там, где продаётся подписка. */
export const packageSalesHere = (packageId: string): boolean =>
    subscriptionSalesHere() || !APP_YEAR_PACKAGE_IDS.includes(packageId);

const markHasApp = (): void => {
    try {
        localStorage.setItem(HAS_APP_KEY, new Date().toISOString());
    } catch {
        /* приватный режим — отметки нет, будет «Установить» */
    }
};

/** Открывало ли приложение окно сайта в этом браузере (отметка isAppWindow). */
export const hasAppMark = (): boolean => {
    try {
        return !!localStorage.getItem(HAS_APP_KEY);
    } catch {
        return false;
    }
};

/** Ссылка, которая открывает приложение MY REVIVE (его intent-filter myrevive://app). */
export const APP_OPEN_URL = "myrevive://app";

/**
 * Вкладка «Календарь» приложения (маршрут calendar; MainActivity знает этот путь с
 * feat/calendar-tab-link). Сборка постарше путь не знает — она просто открывается на том экране,
 * где её оставили, как по [APP_OPEN_URL].
 */
export const APP_CALENDAR_URL = "myrevive://app/calendar";

/**
 * Перейти по ссылке приложения (myrevive://app/…). Только по нажатию: Chrome выпускает ссылку
 * другой схемы из страницы лишь в ответ на жест человека.
 */
export const openAppLink = (url: string): void => {
    window.location.href = url;
};

/**
 * Конструктор плана тренировок в приложении (маршрут plan_wizard; MainActivity знает этот путь с
 * fix/1110-planreminder). Сборка постарше ссылку не знает — она просто возвращает в приложение:
 * окно сайта закрывается, как по «‹».
 */
export const APP_PLAN_WIZARD_URL = "myrevive://app/plan_wizard";

/**
 * Рубильник: выпущенное приложение знает ссылку [APP_PLAN_WIZARD_URL]. Android 2.0.254 — ещё нет
 * (кнопка вернула бы в приложение без конструктора), iOS — не знает вовсе. Пока выключен, в окне
 * приложения подсказка «Создайте план тренировок — …» идёт без кнопки. Включить, когда в Google Play
 * сборка ≥ 2.0.255 (с fix/1110-planreminder); кнопка и тогда — только на Android.
 */
export const APP_WINDOW_PLAN_WIZARD = false;

/**
 * «‹» в окне приложения: есть куда вернуться внутри окна (react-router
 * хранит номер шага в history.state.idx) — на шаг назад; это первая страница
 * окна — обратно в приложение.
 */
export const appWindowBack = (navigateBack: () => void): void => {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === "number" && idx > 0) navigateBack();
    else appWindowClose();
};

/**
 * Закрыть окно сайта и вернуться в приложение — сразу, сколько бы шагов ни
 * было пройдено внутри окна. Так уходит «‹» корня вкладки (правило «назад —
 * на уровень вверх»: выше вкладки — само приложение).
 */
export const appWindowClose = (): void => {
    window.location.href = APP_CLOSE_URL;
};
